import { fabric } from 'fabric';
import { Rect } from 'fabric/fabric-impl';
import { AlignCenterHorizontal, AlignCenterVertical, FlipHorizontal, FlipVertical, Info, SquareRoundCorner } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import alignment from 'ud-ui/components/fabric-canvas/addons/alignment/alignment';
import history from 'ud-ui/components/fabric-canvas/addons/history';
import SnapToGrid from 'ud-ui/components/fabric-canvas/addons/snap-to-grid';
import UDTippy from 'ud-ui/components/tippy';
import { round } from '../../../../domain/utils/helpers';
import { selectActiveObjects, selectFabricManager } from '../../../../store/editor/selectors';
import ColorPicker from '../../elements/color-picker';
import Input, { IconWrapper } from '../../elements/input';
import LayerControls from './components/layer-controls';
import LockPosition from './components/lock-position';
import { EmptyButton, Item, LongDivider, ShortDivider, SubItem } from './styles';

function Figure() {
  const activeObjects = useSelector(selectActiveObjects);
  const fabricManager = useSelector(selectFabricManager);
  const object = activeObjects[0];

  const objectColor = new fabric.Color(object.get('fill')?.toString());
  const scale = object?.getObjectScaling();
  const isRect = object?.type === 'rect';
  const { width, height, top, left } = object?.getBoundingRect(true) ?? defaultBounds;
  const objectRadius = object?.data?.rx ?? 0;
  const lockMovementX = object?.lockMovementX;
  const isTrapezoid = object?.data?.shapeType === 'trapezoid';

  const getTrapezoidBases = () => {
    if (!isTrapezoid) return { topBase: 0, bottomBase: 0 };
    const poly = object as fabric.Polygon;
    const pts = poly.points ?? [];
    const sx = scale?.scaleX ?? 1;
    return {
      topBase: round((pts[1].x - pts[0].x) * sx),
      bottomBase: round((pts[2].x - pts[3].x) * sx),
    };
  };

  const getTrapezoidBasesPercent = (bases: { topBase: number; bottomBase: number }) => ({
    topBasePercent: round((bases.topBase / width) * 100),
    bottomBasePercent: round((bases.bottomBase / width) * 100),
  });

  const [color, setColor] = useState<undefined | string>(objectColor?.toHexa());
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: round(left), y: round(top) });
  const [size, setSize] = useState<{ width: number; height: number }>({ width: round(width), height: round(height) });
  const [radius, setRadius] = useState<number>(objectRadius);
  const [trapezoidBases, setTrapezoidBases] = useState(getTrapezoidBases);
  const [trapezoidBasesPercent, setTrapezoidBasesPercent] = useState(() => getTrapezoidBasesPercent(getTrapezoidBases()));
  const [isPositionLock, setIsPositionLock] = useState(lockMovementX ?? false);

  useEffect(() => {
    setColor(objectColor?.toHexa());
    setPosition({ x: round(left), y: round(top) });
    setSize({ width: round(width), height: round(height) });
    setRadius(objectRadius);
    setIsPositionLock(lockMovementX ?? false);
    const bases = getTrapezoidBases();
    setTrapezoidBases(bases);
    setTrapezoidBasesPercent(getTrapezoidBasesPercent(bases));

    let updatePosition = () => {
      const x = object?.get('left') ?? 0;
      const y = object?.get('top') ?? 0;
      setPosition({ x: round(x), y: round(y) });
    };

    let updateSize = () => {
      const { width, height } = object?.getBoundingRect(true) ?? defaultBounds;
      setSize({ width: round(width), height: round(height) });
    };

    fabricManager?.canvas.on('mouse:up', updatePosition);
    fabricManager?.canvas.on('mouse:up', updateSize);

    return () => {
      fabricManager?.canvas.off('mouse:up', updatePosition);
      fabricManager?.canvas.off('mouse:up', updateSize);
    };

    // eslint-disable-next-line
  }, [activeObjects]);

  const onChangePosition = (x: number, y: number) => {
    object?.setPositionByOrigin(new fabric.Point(x, y), 'left', 'top');
    setPosition({ x, y });
    fabricManager?.canvas.requestRenderAll();
  };

  const onChangeSize = (width: number, height: number) => {
    object?.set({ width: width / (scale?.scaleX ?? 1), height: height / (scale?.scaleY ?? 1) });
    setSize({ width, height });
    fabricManager?.canvas.requestRenderAll();
  };

  const onChangeRadius = (value: number) => {
    if (isRect) {
      if (!object.scaleX || !object.scaleY) return;
      (object as Rect).set({ rx: value * (1 / object.scaleX), ry: value * (1 / object.scaleY) });
      object.data = { ...object.data, rx: value, ry: value };
    }
    setRadius(value);
    fabricManager?.canvas.requestRenderAll();
  };

  const onClickAlignmentCenterVertical = () => {
    const newPos = alignment.single.centerVertical(object);
    if (!newPos) return;
    setPosition({ x: newPos.left, y: newPos.top });
  };

  const onClickAlignmentCenterHorizontal = () => {
    const newPos = alignment.single.centerHorizontal(object);
    if (!newPos) return;
    setPosition({ x: newPos.left, y: newPos.top });
  };

  const onFlipHorizontal = () => {
    object?.set({ flipX: !object.flipX });
    fabricManager?.canvas.requestRenderAll();
    history.saveState();
  };

  const onFlipVertical = () => {
    object?.set({ flipY: !object.flipY });
    fabricManager?.canvas.requestRenderAll();
    history.saveState();
  };

  const onChangeTrapezoidBase = (topBase: number, bottomBase: number) => {
    if (isNaN(topBase) || isNaN(bottomBase)) return;
    const poly = object as fabric.Polygon;
    const pts = poly.points;
    if (!pts) return;
    const sx = scale?.scaleX ?? 1;
    const newTopLocal = topBase / sx;
    const newBottomLocal = bottomBase / sx;
    const topCenterX = (pts[0].x + pts[1].x) / 2;
    const bottomCenterX = (pts[2].x + pts[3].x) / 2;
    const newPoints = [
      new fabric.Point(topCenterX - newTopLocal / 2, pts[0].y),
      new fabric.Point(topCenterX + newTopLocal / 2, pts[1].y),
      new fabric.Point(bottomCenterX + newBottomLocal / 2, pts[2].y),
      new fabric.Point(bottomCenterX - newBottomLocal / 2, pts[3].y),
    ];
    poly.set({ points: newPoints });
    poly.setCoords();
    setTrapezoidBases({ topBase, bottomBase });
    setTrapezoidBasesPercent(getTrapezoidBasesPercent({ topBase, bottomBase }));
    fabricManager?.canvas.requestRenderAll();
    history.saveState();
  };

  const onChangeTrapezoidBasePercent = (topBasePercent: number, bottomBasePercent: number) => {
    if (isNaN(topBasePercent) || isNaN(bottomBasePercent)) return;
    const topBase = SnapToGrid.roundByGrid((topBasePercent / 100) * width);
    const bottomBase = SnapToGrid.roundByGrid((bottomBasePercent / 100) * width);
    onChangeTrapezoidBase(topBase, bottomBase);
  };

  const onChangeColor = (color: string) => {
    setColor(color);
    object?.set({ fill: color });
    fabricManager?.canvas.requestRenderAll();
    history.saveState();
  };

  return (
    <>
      <Item>
        <div>Фигура</div>
      </Item>
      <ShortDivider />
      <Item>
        <SubItem style={{ width: '26%', justifyContent: 'space-between' }}>
          <UDTippy content="По центру по горизонтали" size="xs" placement="bottom" delay={300}>
            <span style={{ display: 'inline-block' }}>
              <EmptyButton onClick={onClickAlignmentCenterHorizontal}>
                <AlignCenterHorizontal size={18} color={'#007AFF'} />
              </EmptyButton>
            </span>
          </UDTippy>
          <UDTippy content="По центру по вертикали" size="xs" placement="bottom" delay={300}>
            <span style={{ display: 'inline-block' }}>
              <EmptyButton onClick={onClickAlignmentCenterVertical}>
                <AlignCenterVertical size={18} color={'#007AFF'} />
              </EmptyButton>
            </span>
          </UDTippy>
          <UDTippy content="Отразить горизонтально" size="xs" placement="bottom" delay={300}>
            <span style={{ display: 'inline-block' }}>
              <EmptyButton onClick={onFlipHorizontal}>
                <FlipHorizontal size={18} color={'#007AFF'} />
              </EmptyButton>
            </span>
          </UDTippy>
          <UDTippy content="Отразить вертикально" size="xs" placement="bottom" delay={300}>
            <span style={{ display: 'inline-block' }}>
              <EmptyButton onClick={onFlipVertical}>
                <FlipVertical size={18} color={'#007AFF'} />
              </EmptyButton>
            </span>
          </UDTippy>
        </SubItem>
        <SubItem>
          <span>Расположение</span>
          <Input
            style={{ width: 73 }}
            icon={<IconWrapper>X</IconWrapper>}
            value={position.x.toString()}
            onChange={(value) => onChangePosition(+value, position.y)}
          />
          <Input
            style={{ width: 73 }}
            icon={<IconWrapper>Y</IconWrapper>}
            value={position.y.toString()}
            onChange={(value) => onChangePosition(position.x, +value)}
          />
        </SubItem>
        <SubItem>
          <span>Размер</span>
          <Input
            icon={<IconWrapper>Ш</IconWrapper>}
            value={size.width.toString()}
            onChange={(value) => onChangeSize(+value, size.height)}
          />
          <Input
            icon={<IconWrapper>В</IconWrapper>}
            value={size.height.toString()}
            onChange={(value) => onChangeSize(size.width, +value)}
          />
        </SubItem>
        {isRect && (
          <SubItem style={{ justifyContent: 'space-between' }}>
            <span>Радиус</span>
            <Input
              icon={
                <IconWrapper>
                  <SquareRoundCorner size={14} />
                </IconWrapper>
              }
              style={{ width: 127 }}
              value={radius.toString()}
              onChange={(value) => {
                onChangeRadius(+value);
              }}
            />
          </SubItem>
        )}
      </Item>
      {isTrapezoid && (
        <>
          <LongDivider />
          <Item>
            <SubItem style={{ justifyContent: 'space-between' }}>
              <span>Основания (px)</span>
              <UDTippy
                size="xs"
                placement="top"
                content={`Значения оснований округляются до ближайшего шага сетки (${SnapToGrid.gridStep} px). При вводе в процентах округление применяется автоматически.`}
              >
                <span style={{ display: 'inline-flex', cursor: 'help', color: '#929292' }}>
                  <Info size={14} />
                </span>
              </UDTippy>
            </SubItem>
            <SubItem style={{ justifyContent: 'space-between' }}>
              <Input
                icon={<IconWrapper>В</IconWrapper>}
                style={{ width: 73 }}
                value={trapezoidBases.topBase.toString()}
                onSubmit={(value) => onChangeTrapezoidBase(+value, trapezoidBases.bottomBase)}
                onBlur={(value) => onChangeTrapezoidBase(+value, trapezoidBases.bottomBase)}
              />
              <Input
                icon={<IconWrapper>Н</IconWrapper>}
                style={{ width: 73 }}
                value={trapezoidBases.bottomBase.toString()}
                onSubmit={(value) => onChangeTrapezoidBase(trapezoidBases.topBase, +value)}
                onBlur={(value) => onChangeTrapezoidBase(trapezoidBases.topBase, +value)}
              />
            </SubItem>
            <SubItem style={{ justifyContent: 'space-between' }}>
              <span>Основания (%)</span>
            </SubItem>
            <SubItem style={{ justifyContent: 'space-between' }}>
              <Input
                icon={<IconWrapper>В</IconWrapper>}
                style={{ width: 73 }}
                value={trapezoidBasesPercent.topBasePercent.toString()}
                onSubmit={(value) => onChangeTrapezoidBasePercent(+value, trapezoidBasesPercent.bottomBasePercent)}
                onBlur={(value) => onChangeTrapezoidBasePercent(+value, trapezoidBasesPercent.bottomBasePercent)}
              />
              <Input
                icon={<IconWrapper>Н</IconWrapper>}
                style={{ width: 73 }}
                value={trapezoidBasesPercent.bottomBasePercent.toString()}
                onSubmit={(value) => onChangeTrapezoidBasePercent(trapezoidBasesPercent.topBasePercent, +value)}
                onBlur={(value) => onChangeTrapezoidBasePercent(trapezoidBasesPercent.topBasePercent, +value)}
              />
            </SubItem>
          </Item>
        </>
      )}
      <LongDivider />
      <Item>
        Заливка / фон
        <ColorPicker color={color?.toString() ?? '#FFFFFFFF'} onChange={onChangeColor} showAlpha />
      </Item>
      <LongDivider />
      <LayerControls object={object} />
      <LockPosition objects={[object]} onChange={setIsPositionLock} value={isPositionLock} />
    </>
  );
}

export default Figure;

const defaultBounds = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
};
